import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { RecentActivityItem } from '@/types/domain/dashboard';
import { StatusBadge } from '@/components/data-display/StatusBadge';
import { formatCurrency } from '@/lib/utils';
import { History, ArrowUpRight } from 'lucide-react';
import { Button } from '@/components/ui/Button';

interface RecentActivityTableProps {
  activities: RecentActivityItem[];
  onViewAll?: () => void;
  onItemClick?: (item: RecentActivityItem) => void;
}

export const RecentActivityTable: React.FC<RecentActivityTableProps> = ({
  activities,
  onViewAll,
  onItemClick,
}) => {
  return (
    <Card className="border-border rounded-2xl shadow-2xs h-full flex flex-col justify-between font-heading">
      <CardHeader className="pb-3 border-b border-border/60">
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading text-sm font-bold text-foreground flex items-center gap-2">
            <History className="h-4 w-4 text-primary" />
            Recent Procurement Activity
          </CardTitle>
          {onViewAll && (
            <Button size="sm" variant="ghost" onClick={onViewAll} className="h-7 text-xs font-semibold text-primary">
              View All <ArrowUpRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="pt-2 flex-1 flex flex-col justify-between">
        <div className="overflow-x-auto custom-scrollbar flex-1">
          <table className="w-full text-xs text-left">
            <thead>
              <tr className="border-b border-border/60 text-foreground-secondary font-heading font-bold">
                <th className="py-2.5 px-3">Transaction ID</th>
                <th className="py-2.5 px-3">Description</th>
                <th className="py-2.5 px-3">Requester / Vendor</th>
                <th className="py-2.5 px-3 text-right">Value</th>
                <th className="py-2.5 px-3">Status</th>
                <th className="py-2.5 px-3 text-right">Time</th>
              </tr>
            </thead>
            <tbody>
              {activities.map((act) => (
                <tr
                  key={act.id}
                  onClick={() => onItemClick && onItemClick(act)}
                  className="border-b border-border/40 hover:bg-muted/40 transition-colors cursor-pointer"
                >
                  <td className="py-3 px-3 font-mono font-bold text-primary">{act.entityNumber}</td>
                  <td className="py-3 px-3 font-semibold text-foreground truncate max-w-[200px]">{act.title}</td>
                  <td className="py-3 px-3 text-foreground-secondary">{act.partyName}</td>
                  <td className="py-3 px-3 text-right font-mono font-bold text-foreground">
                    {formatCurrency(act.amount, act.currency)}
                  </td>
                  <td className="py-3 px-3">
                    <StatusBadge status={act.status} category="PurchaseRequest" />
                  </td>
                  <td className="py-3 px-3 text-right text-foreground-muted text-[11px] font-mono">{act.timestamp}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};
